import _ from "lodash";
import { IReportEntityType, reportEntityTypes, tenantConfiguration } from "shared/constants/reportConstants";
import {
    IEntitiyTypeEntities,
    IMineTenantElements,
    IReport,
    IReportElement,
    ITenantTableColumn,
} from "shared/reports/models/models";
import { UnitType } from "shared/reports/models/UnityType";
import {
    IRankingV3Element_Public,
    IRankingV3Report_Public,
    IRankingV3ReportEntity_Public,
    RankingV3Element_Public,
    RankingV3Element_PublicUnit,
    RankingV3ReportEntity_PublicLevel,
    RankingV3ReportEntity_PublicRankingType,
} from "shared/service/api/generated";

export class RankingReportElement extends RankingV3Element_Public implements IReportElement {
    columnKey: string;
    unitType: UnitType;

    constructor(data?: IRankingV3Element_Public) {
        super(data);
        this.columnKey = `${this.tenant}__${this.title}`;
        this.unitType =
            data?.unit === undefined || data?.unit === RankingV3Element_PublicUnit.STRING
                ? UnitType.STRING
                : data?.unit === RankingV3Element_PublicUnit.CURRENCY
                ? UnitType.CURRENCY
                : data.unit === RankingV3Element_PublicUnit.NUMERIC
                ? UnitType.NUMERIC
                : data?.unit === RankingV3Element_PublicUnit.PERCENTAGE
                ? UnitType.PERCENTAGE
                : UnitType.STRING;
    }

    public get sortValue() {
        return this.position;
    }
}

export class RankingReportEntity {
    rankingType?: RankingV3ReportEntity_PublicRankingType;
    level?: RankingV3ReportEntity_PublicLevel;
    branchCode?: string;
    branchName?: string;
    username?: string;
    userFullName?: string;
    mine?: boolean;
    elementList?: RankingReportElement[];

    constructor(data?: IRankingV3ReportEntity_Public) {
        this.rankingType = data?.rankingType;
        this.level = data?.level;
        this.branchCode = data?.branchCode;
        this.branchName = data?.branchName;
        this.username = data?.username;
        this.userFullName = data?.userFullName;
        this.mine = data?.mine;
        this.elementList = data?.elementList?.map((i) => new RankingReportElement(i));
    }

    public get elements() {
        return this.elementList;
    }
}

export class RankingReport implements IReport {
    effectiveDate?: Date;
    list?: RankingReportEntity[];
    mineElementsByTenant: IMineTenantElements[];
    entitiesByType: IEntitiyTypeEntities<RankingReportEntity>[];

    constructor(data?: IRankingV3Report_Public) {
        const entities = data?.list?.map((i) => new RankingReportEntity(i));

        this.effectiveDate = data?.effectiveDate;
        this.list = data?.list?.map((i) => new RankingReportEntity(i));
        this.mineElementsByTenant = this.getMineReportsByTenant(entities);
        this.entitiesByType = this.getEntitiesByType(entities);
    }

    public get entityTypeList(): IReportEntityType[] {
        return this.entitiesByType.map((i) => i.entityType);
    }

    private getMineReportsByTenant(entities: RankingReportEntity[] | undefined): IMineTenantElements[] {
        const result: IMineTenantElements[] = [];

        const mineElements = entities?.find((i) => i.mine === true)?.elementList;

        tenantConfiguration.forEach((tenantConfiguration) => {
            // Sort once API adds order index
            const elementsByTenant = mineElements?.filter((i) => i.tenant === tenantConfiguration.code);

            if (!elementsByTenant || elementsByTenant.length === 0) {
                return;
            }

            result.push({
                tenant: tenantConfiguration,
                elements: elementsByTenant,
            });
        });

        return result;
    }

    private getEntitiesByType(
        entities: RankingReportEntity[] | undefined
    ): IEntitiyTypeEntities<RankingReportEntity>[] {
        const result: IEntitiyTypeEntities<RankingReportEntity>[] = [];

        reportEntityTypes.forEach((entityType) => {
            const entitiesByType = entities?.filter(
                (i) =>
                    i.rankingType !== undefined &&
                    RankingV3ReportEntity_PublicRankingType[i.rankingType] === entityType.code
            );

            if (entitiesByType === undefined || entitiesByType.length === 0) {
                return;
            }

            result.push({
                entityType: entityType,
                entities: entitiesByType,
                tenantTableColumns: this.getTenantTableColumns(entitiesByType[0].elementList),
            });
        });

        return result;
    }

    private getTenantTableColumns(elements: RankingReportElement[] | undefined): ITenantTableColumn[] {
        const result: ITenantTableColumn[] = [];

        tenantConfiguration.forEach((tenantConfiguration) => {
            const elementsByTenant = _.sortBy(
                _.uniqBy(
                    elements?.filter((i) => i.tenant === tenantConfiguration.code),
                    (i) => i.columnKey
                ),
                (i) => i.title
            );

            if (!elementsByTenant || elementsByTenant.length === 0) {
                return;
            }

            const currentTenantTableColumns: ITenantTableColumn = {
                tenant: tenantConfiguration,
                tableColumns: [],
            };

            elementsByTenant.forEach((reportElement) => {
                currentTenantTableColumns.tableColumns.push({
                    columnKey: reportElement.columnKey,
                    displayName: reportElement.text ?? reportElement.title ?? "",
                });
            });

            result.push(currentTenantTableColumns);
        });

        return result;
    }
}
